import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, type Me } from './api.ts';
import * as native from './native.ts';
import * as passkeys from './passkeys.ts';
import {
  authorizePrivacyChange, MASK as MASK_TEXT, readPrivacyMode, writePrivacyMode,
} from './privacy.ts';
import { formatMoney } from './format.ts';

// Re-exported so screens read the mask from the same module they read the flag.
export { MASK } from './privacy.ts';
import { applyDocumentLocale, translate, translateError, type Locale, type TranslationKey } from './i18n.ts';

/** System + custom categories, fetched once and shared. */
export interface CategoryMeta {
  key: string;
  en: string;
  ar: string;
  icon: string;
  color: string;
  subcategories: { key: string; en: string; ar: string }[];
}

interface AppState {
  user: Me | null;
  setUser: (u: Me | null) => void;
  refreshUser: () => Promise<void>;

  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: TranslationKey) => string;

  categories: CategoryMeta[];
  categoryMeta: (key: string | null | undefined) => CategoryMeta;
  categoryLabel: (key: string | null | undefined) => string;
  subcategoryLabel: (category: string | null | undefined, sub: string | null | undefined) => string | null;

  /**
   * Account every screen is narrowed to, or null for all of them.
   *
   * A filter that changes every figure in the app must survive a relaunch —
   * being silently reset would show a different total for the same question —
   * so it is persisted. Home displays it for the opposite reason: an invisible
   * filter that quietly hides spending is the worst bug a finance app can have.
   */
  activeAccountId: string | null;
  setActiveAccountId: (id: string | null) => void;

  /**
   * Whether privacy mode is on. See `usePrivateMoney` for what it covers.
   *
   * Off unless the owner turned it on, and then on until they turn it off —
   * remembered across launches, so a screen still being shared when the app is
   * reopened does not uncover itself.
   */
  amountsHidden: boolean;
  /**
   * Turns the mode on or off. Turning it off asks for Face ID and returns
   * false if that was refused, so the caller can say so rather than leaving a
   * switch that appears to have done nothing.
   */
  setPrivacyMode: (on: boolean) => Promise<boolean>;

  toast: (message: string, kind?: 'info' | 'error') => void;
  /**
   * Shows a failed request, in the reader's language.
   *
   * Separate from `toast` because an API failure is the one message the client
   * does not compose: it arrives as English prose with a stable error code, and
   * the code is the part that can be translated. Every call site passing
   * `err.message` straight to `toast` was a guaranteed English toast on an
   * Arabic screen.
   */
  toastError: (error: unknown) => void;
}

const Ctx = createContext<AppState | null>(null);

const FALLBACK_CATEGORY: CategoryMeta = {
  key: 'other', en: 'Other', ar: 'أخرى', icon: '❓', color: '#9ca3af', subcategories: [],
};

const LOCALE_KEY = 'fintrack.locale';
const ACCOUNT_KEY = 'fintrack.account';

function initialAccount(): string | null {
  try { return localStorage.getItem(ACCOUNT_KEY); } catch { return null; }
}

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_KEY);
    if (stored === 'ar' || stored === 'en') return stored;
  } catch { /* ignore */ }
  return navigator.language?.startsWith('ar') ? 'ar' : 'en';
}

export function AppProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<Me | null>(null);
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [activeAccountId, setActiveAccountIdState] = useState<string | null>(initialAccount);

  const setActiveAccountId = useCallback((id: string | null) => {
    setActiveAccountIdState(id);
    try {
      if (id) localStorage.setItem(ACCOUNT_KEY, id);
      else localStorage.removeItem(ACCOUNT_KEY);
    } catch { /* private browsing */ }
  }, []);
  const [categories, setCategories] = useState<CategoryMeta[]>([]);
  const [toastState, setToastState] = useState<{ message: string; kind: 'info' | 'error' } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  // Read inside stable callbacks without making them depend on `user`.
  const userRef = useRef<Me | null>(null);
  userRef.current = user;
  // Same reason, for the locale: toastError must not be rebuilt on every
  // language change, or every component holding it re-renders for nothing.
  const localeRef = useRef<Locale>(locale);
  localeRef.current = locale;

  const [amountsHidden, setAmountsHidden] = useState(readPrivacyMode);

  /**
   * Turns privacy mode on or off.
   *
   * Switching it *on* is free — covering your own figures needs no permission,
   * and asking for any would put a delay between deciding to hide something and
   * it being hidden.
   *
   * Switching it *off* proves who is holding the phone. That is the whole
   * substance of the mode: without it, anyone handed the phone with the figures
   * covered can uncover them from this same screen, and the switch protects
   * nothing but the owner's memory.
   *
   * The same two routes the app lock uses, for the same reasons: in the native
   * shell a local biometric check, which needs no network; on the web a passkey
   * assertion, which is what puts Face ID in front of a browser. A device with
   * neither falls through to uncovering — refusing would leave that person
   * permanently unable to see their own balance, which is a worse failure than
   * the one this guards against.
   */
  const setPrivacyMode = useCallback(async (on: boolean) => {
    const ok = await authorizePrivacyChange(on, async () => {
      if (native.isNative()) {
        return native.biometricUnlock(
          localeRef.current === 'ar' ? 'إظهار مبالغك' : 'Show your amounts',
        );
      }
      // A device with no passkey falls through rather than refusing: leaving
      // somebody permanently unable to see their own balance is a worse
      // failure than the one this guards against.
      if (!passkeys.supported() || !(await passkeys.platformAuthenticatorAvailable())) return true;
      try {
        await passkeys.login();
        return true;
      } catch {
        // Cancelled, or no passkey on this device. The figures stay covered,
        // and the caller says so rather than silently doing nothing.
        return false;
      }
    });

    if (!ok) return false;
    setAmountsHidden(on);
    writePrivacyMode(on);
    return true;
  }, []);


  useEffect(() => {
    applyDocumentLocale(locale);
    try { localStorage.setItem(LOCALE_KEY, locale); } catch { /* ignore */ }
  }, [locale]);

  // The signed-in user's stored locale wins over the browser guess.
  useEffect(() => {
    if (user?.locale && user.locale !== locale) setLocaleState(user.locale);
    // Intentionally keyed on the user's id: a later manual switch must stick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get<{ system: CategoryMeta[]; custom: CategoryMeta[] }>('/categories')
      .then((res) => { if (!cancelled) setCategories([...res.system, ...res.custom]); })
      .catch(() => { /* categories fall back to defaults */ });
    return () => { cancelled = true; };
  }, [user?.id]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    // Persist server-side so the choice follows the account rather than the
    // device — but only once signed in; on the auth screen there is nobody to
    // save it for, and calling /me there would just 401.
    if (userRef.current) {
      api.patch('/me', { locale: l }).catch(() => { /* offline: local choice still applies */ });
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const me = await api.get<Me>('/me');
    setUser(me);
  }, []);

  /**
   * Keep the account's day boundary on the device's timezone.
   *
   * The server decides what "today" means — spent today, the daily allowance,
   * every chart bucket — from tz_offset_minutes on the user row. Nothing ever
   * sent it, so every account sat on the column default of +4, and the app
   * meanwhile labelled rows "Today" or "Yesterday" from the *browser's* clock.
   * For anyone outside the Gulf those are different days: a transaction could
   * read "Today" in the list and be missing from Spent today, which is the app
   * disagreeing with itself in the one place it must not.
   *
   * getTimezoneOffset counts minutes *behind* UTC, so it is negated to match
   * the column's convention of minutes ahead. Only written when it has actually
   * changed — a PATCH on every launch to say nothing moved is noise.
   */
  useEffect(() => {
    if (!user) return;
    const deviceOffset = -new Date().getTimezoneOffset();
    if (deviceOffset === user.tzOffsetMinutes) return;
    api.patch('/me', { tzOffsetMinutes: deviceOffset })
      .then(() => { setUser({ ...user, tzOffsetMinutes: deviceOffset }); })
      .catch(() => { /* offline: the stored offset stands until next launch */ });
  }, [user]);

  const toastError = useCallback((error: unknown) => {
    const e = error as { code?: string; message?: string };
    window.clearTimeout(toastTimer.current);
    setToastState({
      message: translateError(localeRef.current, {
        code: e?.code,
        message: e?.message || translate(localeRef.current, 'somethingWrong'),
      }),
      kind: 'error',
    });
    toastTimer.current = window.setTimeout(() => setToastState(null), 3800);
  }, []);

  const toast = useCallback((message: string, kind: 'info' | 'error' = 'info') => {
    window.clearTimeout(toastTimer.current);
    setToastState({ message, kind });
    toastTimer.current = window.setTimeout(() => setToastState(null), 3800);
  }, []);

  const categoryIndex = useMemo(
    () => new Map(categories.map((c) => [c.key, c])),
    [categories],
  );

  const value = useMemo<AppState>(() => {
    const categoryMeta = (key: string | null | undefined): CategoryMeta =>
      (key && categoryIndex.get(key)) || FALLBACK_CATEGORY;

    return {
      user, setUser, refreshUser,
      locale, setLocale,
      t: (key: TranslationKey) => translate(locale, key),
      categories,
      categoryMeta,
      categoryLabel: (key) => {
        const meta = categoryMeta(key);
        return locale === 'ar' ? meta.ar : meta.en;
      },
      activeAccountId, setActiveAccountId,
      subcategoryLabel: (category, sub) => {
        if (!sub) return null;
        const found = categoryMeta(category).subcategories.find((s) => s.key === sub);
        if (!found) return sub.replace(/_/g, ' ');
        return locale === 'ar' ? found.ar : found.en;
      },
      amountsHidden, setPrivacyMode,
      toast,
      toastError,
    };
  }, [user, locale, categories, categoryIndex, refreshUser, setLocale, toast, toastError,
      activeAccountId, setActiveAccountId,
      amountsHidden, setPrivacyMode]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {toastState && (
        <div className={`toast${toastState.kind === 'error' ? ' toast--error' : ''}`} role="status" aria-live="polite">
          {toastState.message}
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useApp(): AppState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}

type MoneyFormatter = (
  minor: number | null | undefined,
  currency: string,
  opts?: { compact?: boolean; bare?: boolean; signed?: boolean },
) => string;

/**
 * Formats an amount.
 *
 * Exists rather than calling `formatMoney` directly so the reader's locale is
 * filled in — every site passed it, and forgetting it silently formats an
 * Arabic screen in English.
 */
export function useMoney(): MoneyFormatter {
  const { locale } = useApp();
  return useCallback(
    (minor, currency, opts = {}) => formatMoney(minor, currency, { locale, ...opts }),
    [locale],
  );
}

/**
 * The same, for a figure that is nobody else's business.
 *
 * What the switch is for is handing someone the phone to explain the app, and
 * the line it draws is between what you spend and what you have. A coffee at
 * AED 22 is a conversation; what you earn, what is left to you this month and
 * what you are putting away are not. So the covered set is exactly three
 * things, and the call sites here are what defines them:
 *
 *   income        — the totals, the monthly income setting, the last detected
 *                   salary, salary credits in the ledger, and income minus
 *                   spending, which is the same fact told backwards while
 *                   spending is on screen beside it
 *   safe to spend — the headline, the daily allowance, and what is left of
 *                   today's share, which is that allowance minus a figure
 *                   printed on the same card
 *   saving        — the target, what it takes from this cycle, and the balance
 *                   and target of every savings goal
 *
 * Spending is deliberately outside it. Covering that too was tried and made
 * the app unreadable to its own owner: a Plan screen of bullets tells nobody
 * anything, including the person it belongs to. Budgets stay visible for the
 * same reason — a limit on restaurants says nothing about what anyone has.
 */
export function usePrivateMoney(): MoneyFormatter {
  const { amountsHidden, locale } = useApp();
  return useCallback(
    (minor, currency, opts = {}) =>
      amountsHidden ? MASK_TEXT : formatMoney(minor, currency, { locale, ...opts }),
    [amountsHidden, locale],
  );
}

/**
 * Small data-fetching hook. Deliberately not a caching library: the app has a
 * handful of screens and refetch-on-mount is both correct and simple here.
 */
/**
 * How stale data may be before returning to the app refetches it.
 *
 * Not zero: switching apps for a moment should not re-request everything. Not
 * long either — this is a ledger that changes while the app is closed, since
 * transactions arrive from a Shortcut rather than from anything the user did
 * in here.
 */
const REVALIDATE_AFTER_MS = 30_000;

/**
 * How often a screen refreshes while you are sitting on it.
 *
 * Resume events covered coming *back* to the app. Nothing covered staying in
 * it, so a screen left open was frozen at whatever it fetched on mount — for
 * as long as it was open. Two ways that shows up, both of them as the app
 * quietly lying:
 *
 *   - a transaction arrives from the iPhone Shortcut while the app is in the
 *     foreground, and the totals do not move;
 *   - midnight passes, and "Spent today" goes on reporting yesterday.
 *
 * The interval only fires while the document is visible, and still defers to
 * REVALIDATE_AFTER_MS, so a screen that was just loaded is not re-fetched.
 */
const POLL_WHILE_VISIBLE_MS = 60_000;

export function useFetch<T>(
  path: string | null,
  deps: unknown[] = [],
): { data: T | null; error: string | null; loading: boolean; reload: () => Promise<void> } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [nonce, setNonce] = useState(0);
  const loadedAt = useRef(0);
  const silent = useRef(false);
  // Callers that need to know when a reload has actually finished — chiefly
  // pull-to-refresh, whose spinner must outlast the request rather than
  // vanishing the instant the fetch is scheduled.
  const pending = useRef<(() => void)[]>([]);
  const settle = () => { pending.current.splice(0).forEach((resolve) => resolve()); };

  useEffect(() => {
    if (!path) { setLoading(false); return; }
    let cancelled = false;
    // A background revalidation must not blank a screen that is being read.
    if (!silent.current) setLoading(true);
    setError(null);

    api.get<T>(path)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
        loadedAt.current = Date.now();
        silent.current = false;
        settle();
      })
      .catch((err: Error) => {
        if (cancelled) return;
        // A failed background refresh keeps the data already on screen: stale
        // figures the user can see beat an error page replacing them.
        if (!silent.current) setError(err.message);
        setLoading(false);
        silent.current = false;
        settle();
      });

    return () => { cancelled = true; settle(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  // Refetch when the app returns to the foreground.
  //
  // Installed to the Home Screen, this app is suspended and resumed rather than
  // reloaded, so a mount-only fetch leaves the totals frozen at whatever they
  // were when it was first opened — for days, while transactions keep arriving
  // from the iPhone Shortcut. Reading a stale balance in a finance app is worse
  // than reading none.
  useEffect(() => {
    if (!path) return;

    const revalidate = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - loadedAt.current < REVALIDATE_AFTER_MS) return;
      silent.current = true;
      setNonce((n) => n + 1);
    };

    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', revalidate);
    const timer = window.setInterval(revalidate, POLL_WHILE_VISIBLE_MS);
    return () => {
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', revalidate);
      window.clearInterval(timer);
    };
  }, [path]);

  return {
    data,
    error,
    loading,
    reload: () =>
      new Promise<void>((resolve) => {
        if (!path) { resolve(); return; }
        pending.current.push(resolve);
        silent.current = false;
        setNonce((n) => n + 1);
      }),
  };
}
